import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';

const LanguageSelector = () => {
  const [currentLanguage, setCurrentLanguage] = useState('ar');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    {
      code: 'ar',
      name: 'العربية',
      nameEn: 'Arabic',
      flag: '🇸🇾',
      image: '/assets/images/syrialogo.webp',
      dir: 'rtl'
    },
    // {
    //   code: 'en',
    //   name: 'English',
    //   nameEn: 'English',
    //   flag: '🇺🇸',
    //   dir: 'ltr'
    // },
    {
      code: 'tr',
      name: 'Türkçe',
      nameEn: 'Turkish',
      flag: '🇹🇷',
      dir: 'ltr'
    }
  ];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);
    applyLanguageSettings(savedLanguage);

    // Listen for language changes from other sources
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
      applyLanguageSettings(newLanguage);
      // Note: Scroll restoration is handled by the component that initiates the change
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const applyLanguageSettings = (languageCode) => {
    const language = languages?.find(lang => lang?.code === languageCode);
    if (language) {
      document.documentElement.dir = language?.dir;
      document.documentElement.lang = languageCode;
      
      // Update CSS custom properties for RTL/LTR
      if (language?.dir === 'rtl') {
        document.documentElement?.style?.setProperty('--text-align', 'right');
        document.documentElement?.style?.setProperty('--flex-direction', 'row-reverse');
      } else {
        document.documentElement?.style?.setProperty('--text-align', 'left');
        document.documentElement?.style?.setProperty('--flex-direction', 'row');
      }
    }
  };

  const handleLanguageChange = (languageCode) => {
    // Save current scroll position before any changes
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    
    setCurrentLanguage(languageCode);
    localStorage.setItem('memorial-language', languageCode);
    applyLanguageSettings(languageCode);
    setIsOpen(false);
    
    // Trigger a custom event to notify other components
    window.dispatchEvent(new CustomEvent('languageChanged', { 
      detail: { language: languageCode } 
    }));
    
    // Restore scroll position after DOM updates
    // Use multiple requestAnimationFrame to ensure DOM has fully updated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({
          left: scrollX,
          top: scrollY,
          behavior: 'auto' // Instant scroll, no animation
        });
      });
    });
  };

  const currentLang = languages?.find(lang => lang?.code === currentLanguage);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 px-3 py-2 bg-card border border-border rounded-lg hover:bg-muted memorial-transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label="Select language"
      >
        {currentLang?.image ? (
          <AppImage 
            src={currentLang.image} 
            alt={currentLang.nameEn}
            className="w-5 h-5 object-contain"
          />
        ) : (
          <span className="text-lg">{currentLang?.flag}</span>
        )}
        <span className="font-body text-sm font-medium text-text-primary hidden sm:inline">
          {currentLang?.name}
        </span>
        <Icon 
          name={isOpen ? 'ChevronUp' : 'ChevronDown'} 
          size={16} 
          className="text-text-secondary memorial-transition" 
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full mt-2 right-0 w-48 bg-card border border-border rounded-lg shadow-memorial overflow-hidden z-50"
          >
            {languages?.map((language) => (
              <button
                key={language?.code}
                onClick={() => handleLanguageChange(language?.code)}
                className={`w-full flex items-center space-x-4 px-4 py-3 text-left memorial-transition hover:bg-muted focus:outline-none focus:bg-muted ${
                  currentLanguage === language?.code 
                    ? 'bg-primary/5 text-primary' :'text-text-primary'
                }`}
              >
                {language?.image ? (
                  <AppImage 
                    src={language.image} 
                    alt={language.nameEn}
                    className="w-5 h-5 object-contain"
                  />
                ) : (
                  <span className="text-lg">{language?.flag}</span>
                )}
                <div className="flex-1">
                  <div className="font-body text-sm font-medium">
                    {language?.name}
                  </div>
                  <div className="font-body text-xs text-text-secondary">
                    {language?.nameEn}
                  </div>
                </div>
                {currentLanguage === language?.code && (
                  <Icon name="Check" size={16} className="text-primary" />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;